import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Product} from "../../../Model/Catalog/Product";
import {TranslateService} from "@ngx-translate/core";
import {UserManagementService} from "../../../Services/userManagment.service";
import {ValidationService} from "../../../Services/Validation/validation.service";
import {LoginService} from "../../../Services/login.service";
import {CatalogService} from "../../../Services/CatalogManagement/catalog.service";
import {SystemService} from "../../../Services/system.service";
import {FeatureSystem} from "../../../Model/Catalog/FeatureModels/FeatureSystem";
import {ModelService} from "../../../Services/model.service";
import {ValidFeatureService} from "../../../Services/validFeature.service";
import {Feature} from "../../../Model/Catalog/FeatureModels/Feature";
import {FeatureValue} from "../../../Model/classification/FeatureValue";

@Component({
  selector: 'np-product-texts',
  standalone: false,
  templateUrl: './texts.component.html',
  styleUrl: './texts.component.css'
})
export class TextsComponent  implements OnInit {

  @Input() model: Product = new Product();
  @Input() showLinkButtons: Boolean = false;
  
  @Input() showToolTips: Boolean = false;
  @Input() showProToolTips: Boolean = false;
  @Input() showPlaceHolders: Boolean = false;
  
  @Input() isPrint: Boolean = false;
  @Input() productTranslation: Product = null;
  @Input() selectedTranslateLanguage: string = 'deu';
  @Output() onValueChanged = new EventEmitter<any>();
  @Output() translationUpdateFunction = new EventEmitter<{ txtOrg: string; txtTran: string }>();

  @Input() templateView: Boolean = false;
  @Input() massDataChangeMode: boolean = false;
  @Input() showHTMLSwitch: Boolean = true;
  @Input() selectedElements: string[] = [''];
  @Output() elementSelected = new EventEmitter<string>();

  @Input() showDragHandle: boolean = false;
  @Input() dragFilter: string = 'noDrag';

  @Output() excelElementDropped = new EventEmitter<any>();

  constructor(
    public modelService: ModelService,
    public translate: TranslateService,
    public userManagement: UserManagementService,
    public validationService: ValidationService,
    public loginService: LoginService,
    public catalogService: CatalogService,
    public systemService: SystemService,
    public validFeatureService: ValidFeatureService,
  ) {
    this.createNewBenefitFeature = this.createNewBenefitFeature.bind(this);
    this.createNewTopFeature = this.createNewTopFeature.bind(this);
  }

  public marketingClaim: string = "";
  public benefitFeatures: Feature[] = [];
  public topFeatures: Feature[] = [];
  public selectedBenefitFeature: Feature;
  public selectedTopFeature: Feature;
  public wawiFieldsForTextsAvailable = false;
  
  ngOnInit() {
    this.initMarketingClaim();
    let wawiCustomFields = this.loginService.wawiSettings?.customFields?.filter(x => x.tabOption === "Texte");
    let wawiLinesForTexts = 
      this.loginService.wawiSettings?.line1TabOption === "Texte" ||
      this.loginService.wawiSettings?.line2TabOption === "Texte" ||
      this.loginService.wawiSettings?.line3TabOption === "Texte" ||
      this.loginService.wawiSettings?.line4TabOption === "Texte";
    this.wawiFieldsForTextsAvailable = (wawiCustomFields?.length > 0) || wawiLinesForTexts;
    this.initFeatures('udf_NMTOPFEATURES-1.0');
    this.initFeatures('udf_NMBENEFITS-1.0');
  }

  private initMarketingClaim() {
    let system = this.model?.featureSystems?.find(x => x.referenceFeatureSystemName === 'udf_NMMARKETINGCLAIM-1.0');
    if (system && system.features?.length > 0 && system.features[0].featureValues?.length > 0) {
      this.marketingClaim = system.features[0].featureValues[0].value ?? "";
    }
  }

  private initFeatures(featureSystemName: string) {
    let system = this.model?.featureSystems?.find(x => x.referenceFeatureSystemName === featureSystemName);
    if (system && system.features?.length > 0) {
      if(featureSystemName === 'udf_NMBENEFITS-1.0') {
        this.benefitFeatures = system.features;
      }
      else if(featureSystemName === 'udf_NMTOPFEATURES-1.0') {
        this.topFeatures = system.features;     
      }
    }
  }

  public async createNewBenefitFeature(event?: any) {
    await this.createNewFeature('udf_NMBENEFITS-1.0', event);
  }

  public async createNewTopFeature(event?: any) {
    await this.createNewFeature('udf_NMTOPFEATURES-1.0', event);
  }

  public async updateBenefitFeatureValue(event: any, feature: Feature) {
    await this.updateFeatureValue(event, feature, 'udf_NMBENEFITS-1.0');
  }

  public async updateTopFeatureValue(event: any, feature: Feature) {
    await this.updateFeatureValue(event, feature, 'udf_NMTOPFEATURES-1.0');
  }

  private getFeatureList(featureSystemName: string): Feature[] {
    if (featureSystemName === 'udf_NMBENEFITS-1.0') {
      return this.benefitFeatures;
    }

    if (featureSystemName === 'udf_NMTOPFEATURES-1.0') {
      return this.topFeatures;
    }

    return [];
  }

  private setFeatureList(featureSystemName: string, features: Feature[]): void {
    if (featureSystemName === 'udf_NMBENEFITS-1.0') {
      this.benefitFeatures = features;
      return;
    }

    if (featureSystemName === 'udf_NMTOPFEATURES-1.0') {
      this.topFeatures = features;
    }
  }

  public async createNewFeature(featureSystemName: string, event?: any) {
    const existingFeatureSystem = this.model.featureSystems.find(
      x => x.referenceFeatureSystemName === featureSystemName
    );

    if (!existingFeatureSystem) {
      const newFeatureSystem = await this.createFeatureSystem(featureSystemName);
      this.model.featureSystems.push(newFeatureSystem);
      this.setFeatureList(featureSystemName, newFeatureSystem.features);
    } else {
      const currentFeatures = existingFeatureSystem.features ?? [];
      const newFeature = new Feature();

      newFeature.forder =
        (currentFeatures.reduce((maxOrder, f) => Math.max(maxOrder, f.forder ?? 0), 0) ?? 0) + 1;
      newFeature.value = "";

      existingFeatureSystem.features.push(newFeature);
      this.setFeatureList(featureSystemName, existingFeatureSystem.features);
    }

    await this.update(event);
  }

  public async updateFeatureValue(event: any, feature: Feature, featureSystemName: string) {
    const features = this.getFeatureList(featureSystemName);
    const updatedFeature = features.find(f => f.validFeatureId === feature.validFeatureId);

    if (updatedFeature) {
      updatedFeature.value = event.value;

      const featureValue = new FeatureValue();
      featureValue.value = event.value;
      updatedFeature.featureValues = [featureValue];
    }

    await this.update(event);
  }
  
  get hasTranslation(): Boolean {
    if (this.productTranslation == null) {
      return false;
    }
    return true;
  }

  private _showHTMLEditor: Boolean = true;

  get showHTMLEditor() {
    return this._showHTMLEditor;
  }

  set showHTMLEditor(value) {
    this._showHTMLEditor = value;
  }

  showHTMLEditorChanged() {
    this.catalogService
      .getProduct(this.model.id, this.model.customerId, this.catalogService.catalog.id)
      .subscribe(() => {
        this.model = this.catalogService.product;
      });
  }
  
  public translationUpdate(txtOrg, txtTran) {
    this.translationUpdateFunction.emit({ txtOrg: txtOrg, txtTran: txtTran });
  }
  
  public elementSelectFunction(event) {
    this.elementSelected.emit(event);
  }

  public excelElementDrop(e) {
    this.excelElementDropped.emit(e);
  }

  async updateMarketingClaim(event) {
    this.marketingClaim = event.value;
    let existingFeatureSystem = this.model.featureSystems.filter(x => x.referenceFeatureSystemName === 'udf_NMMARKETINGCLAIM-1.0');
    if(existingFeatureSystem.length > 0) {
      let system = existingFeatureSystem[0];
      if(system.features.length == 0) {
        let feature = new Feature();
        feature.value = event.value;       
        let featureValue = new FeatureValue();
        featureValue.value = event.value;
        feature.featureValues = [featureValue];        
        system.features.push(feature);
      }
      else {
        let feature = system.features[0];
        feature.value = event.value;      
        let featureValue = feature.featureValues[0];
        featureValue.value = event.value;
      }
    }
    else {
      let marketingClaimFeatureSystem = await this.createFeatureSystem('udf_NMMARKETINGCLAIM-1.0');
      let featureValue = new FeatureValue();
      featureValue.value = event.value;
      marketingClaimFeatureSystem.features[0].featureValues = [featureValue];
      this.model.featureSystems.push(marketingClaimFeatureSystem);
    }
    await this.update(event) 
  }

  private async createFeatureSystem(featureSystemName: string): Promise<FeatureSystem> {
    
      let validFeatureSystemId =  await this.validFeatureService.getValidIdForSystemName(
        featureSystemName,
        this.loginService.currentCustomer.id
      );

      let newFeatureSystem = new FeatureSystem();
      newFeatureSystem.referenceFeatureSystemName = featureSystemName;
      newFeatureSystem.validFeatureSystemId = validFeatureSystemId;
      newFeatureSystem.order =
        (this.model.featureSystems.reduce((maxOrder, fs) => Math.max(maxOrder, fs.order ?? 0), 0) ?? 0) + 1;

      let feature = new Feature();
      feature.value = "";
      feature.forder = 1;
      newFeatureSystem.features.push(feature);
      return newFeatureSystem;
    
  }
  
  async update(event = null, field?: string, system: string = '-1', element: string = '-1') {
    if (!event.event && !event.toggleLinkedFields && !event.dField)
      return;

    if (event) {
      event.dField = field;
      event.dSystem = system;
      event.dElement = element;
      event.createTemplateItem = true;
    }

    this.onValueChanged.emit(event);
  }

  async onFeaturesReorder(event: any, featureSystemName: string) {
    const fromIndex = event.fromIndex;
    const toIndex = event.toIndex;

    if (fromIndex === toIndex) {
      return;
    }

    const features = [...this.getFeatureList(featureSystemName)];
    const [moved] = features.splice(fromIndex, 1);
    features.splice(toIndex, 0, moved);

    features.forEach((feature, index) => {
      feature.forder = index + 1;
    });

    this.setFeatureList(featureSystemName, features);

    const featureSystem = this.model.featureSystems.find(
      x => x.referenceFeatureSystemName === featureSystemName
    );

    if (featureSystem) {
      featureSystem.features = features;
    }

    await this.update(event);
  }
  
  onBenefitFeatureRowClick(benefitFeature: Feature): void {
    this.selectedBenefitFeature = benefitFeature;
  }

  onTopFeatureRowClick(topFeature: Feature): void {
    this.selectedTopFeature = topFeature;
  }
  
  async onFeatureDeleteClick(feature: Feature, event: any, featureSystemName: string) {
    const featureSystem = this.model.featureSystems.find(
      x => x.referenceFeatureSystemName === featureSystemName
    );

    let updatedFeatures: Feature[];

    if (featureSystem?.features) {
      updatedFeatures = featureSystem.features.filter(f => f.validFeatureId !== feature.validFeatureId);
      featureSystem.features = updatedFeatures;
    } else {
      updatedFeatures = this.getFeatureList(featureSystemName).filter(f => f.validFeatureId !== feature.validFeatureId);
    }

    updatedFeatures.forEach((item, index) => {
      item.forder = index + 1;
    });

    this.setFeatureList(featureSystemName, updatedFeatures);

    if (featureSystemName === 'udf_NMBENEFITS-1.0' && this.selectedBenefitFeature?.validFeatureId === feature.validFeatureId) {
      this.selectedBenefitFeature = null;
    }

    if (featureSystemName === 'udf_NMTOPFEATURES-1.0' && this.selectedTopFeature?.validFeatureId === feature.validFeatureId) {
      this.selectedTopFeature = null;
    }

    await this.update(event);
  }
  
  public showClearButton(field: string): boolean {
    return !this.readOnlyForLinkedFields(field) && !this.templateView;
  }

  public readOnlyForLinkedFields(field: string, system: string = '-1', element: string = '-1'): boolean {
    if (field != null) {
      var isLinked = this.model.isFieldLinked(field, system, element);
      if (isLinked && this.model.isChild != null && this.model.isChild) {
        return true;
      }
    }
    return false;
  }

  isVirtualCatalog(): boolean {
    return this.catalogService?.catalog?.isVirtual;
  }
  
  readOnly(name: string): boolean {
    return this.isVirtualCatalog() || !this.userManagement.canEdit(name);
  }

}
